import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import InstantWinDefaultBanner from "assets/img/instant_win_default_banner.png";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    borderRadius: 16,
    minHeight: 143,
    display: "flex",
    justifyContent: "space-between",
    scrollbarWidth: "thin",
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  label: {
    background:
      "linear-gradient(90deg, #6BDA8E 0%, #4D98D9 34.8%, #7077F7 62.65%, #CB62F6 100%);",
    padding: "2px 8px",
    borderRadius: 8,
    textTransform: "uppercase",
    fontWeight: 900,
    color: "white",
    width: "fit-content",
    textShadow: "-1px 0 #67D099, 1px 0 #949BC8, 1px 0 #AA80DF, 1px 0 #C065F6",
  },
  bannerContainer: {
    borderRadius: 16,
    padding: 16,
    position: "relative",
    background: "linear-gradient(95.48deg, #1A385E 50.93%, #461E70 100%)",
    color: "white",
    overflow: "hidden",
  },
  bannerDefaultImg: {
    position: "absolute",
    width: "50%",
    height: "90%",
    right: -50,
    bottom: -50,
    "& img": {
      height: "100%",
      width: "100%",
      objectFit: "contain",
    },
  },
  defaultThemeBannerCountdown: {
    position: "absolute",
    right: 0,
    top: 8,
    backgroundColor: "#FFFFFFCC",
    color: "black",
    padding: "2px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  bannerImg: {
    width: "100%",
    height: "auto",
    borderRadius: 8,
  },
  bannerCountdown: {
    position: "absolute",
    right: 0,
    top: 10,
    backgroundColor: "#1A385EB2",
    color: "white",
    padding: "4px 12px",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#00000091",
    width: "100%",
    height: "100%",
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
  },
  button: {
    animation: "$breathing 4s ease-in-out infinite normal",
    willChange: "transform",
    width: "100%",
    height: 40,
    border: "none",
    borderRadius: 10,
    background: "linear-gradient(to right, #77530a, #ffd277, #77530a, #77530a, #ffd277, #77530a)",
    backgroundSize: "250%",
    backgroundPosition: "left",
    color: "#ffd277",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transitionDuration: "1s",
    overflow: "hidden",
    fontFamily: "Arial, Helvetica, sans-serif",
    textDecoration: "none",
    userSelect: "none",
    fontSize: 15,
    "&::before": {
      position: "absolute",
      content: '""',
      inset: "3px",
      borderRadius: 8,
      backgroundColor: "#800000",
    },
    "& span": {
      zIndex: 1,
      display: "block",
    },
    "&:hover": {
      backgroundPosition: "right",
      transitionDuration: "1s",
    },
    "&:hover::before": {
      backgroundPosition: "right",
      transitionDuration: "1s",
    },
    "&:active": {
      transform: "scale(0.95)",
    },
    "&:disabled": {
      animation: "none",
      pointerEvents: "none",
      opacity: 0.65,
      color: "#7e7e7e",
      background: "#dcdcdc",
      boxShadow: "none",
      textShadow: "none",
    },
  },
  "@keyframes breathing": {
    "0%": {
      transform: "scale(0.95)",
    },
    "25%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(0.95)",
    },
    "75%": {
      transform: "scale(1)",
    },
    "100%": {
      transform: "scale(0.95)",
    },
  },
}));

export default function InstantWinComponent({
  instantWin,
  lang,
  isLoading,
  countdown,
  getTranslation
}) {
  const classes = useStyles();

  let isJoinStart = instantWin
    ? moment().isAfter(moment.utc(instantWin.join_open_at).local())
    : false;
  let isJoinEnd = instantWin?.join_close_at
    ? moment().isAfter(moment.utc(instantWin.join_close_at).local())
    : false;
  let isEnded = instantWin?.end_at
    ? moment().isAfter(moment.utc(instantWin.end_at).local())
    : false;

  let showCountdown =
    (countdown.day > 0 ||
      countdown.hour > 0 ||
      countdown.min > 0 ||
      countdown.sec > 0) &&
    !isEnded;

  const formatTwoDigits = (value) => {
    return Number(value) < 10 ? `0${value}` : value;
  };

  return (
    <Box p={2}>
      <Box className={classes.container}>
        <Box style={{ width: "100%" }}>
          {isLoading ? (
            <Box
              p={2}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              style={{ width: "100%" }}
            >
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
              <Skeleton width={"100%"} height={30} />
            </Box>
          ) : instantWin ? (
            !instantWin.image_url ? (
              <Box className={classes.bannerContainer}>
                <Box className={classes.label}>
                  <Typography variant="h6" style={{ fontSize: 18 }}>
                    <b>{getTranslation("label.INSTANT_WIN")}</b>
                  </Typography>
                </Box>
                <Box style={{ width: "60%" }} mt={2}>
                  <Box mb={0.5}>
                    <Typography variant="h6">
                      <b>{instantWin.title}</b>
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {instantWin.description}
                  </Typography>
                </Box>
                <Box
                  style={{ width: "60%" }}
                  display="flex"
                  alignItems="center"
                  mt={3}
                  mb={2}
                >
                  <button
                    className={classes.button}
                    style={{
                      border: "1px solid black",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{ textTransform: "capitalize", zIndex: 1 }}
                    >
                      <b>
                        {getTranslation(
                          isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
                        )}
                      </b>
                    </Typography>
                  </button>
                </Box>
                <Box className={classes.bannerDefaultImg}>
                  <img src={InstantWinDefaultBanner} alt="ld" />
                </Box>
                {showCountdown && (!isJoinStart || !isJoinEnd) && (
                  <Box className={classes.defaultThemeBannerCountdown}>
                    <Typography variant="caption">
                      {isJoinStart
                        ? getTranslation("label.ENDS_IN")
                        : getTranslation("label.STARTS_IN")}{" "}
                      <span>
                        <b>
                          {countdown.day}:{formatTwoDigits(countdown.hour)}:
                          {formatTwoDigits(countdown.min)}:
                          {formatTwoDigits(countdown.sec)}
                        </b>
                      </span>
                    </Typography>
                  </Box>
                )}
                {isEnded && (
                  <Box className={classes.overlay}>
                    <Typography variant="body1">
                      {getTranslation("label.ENDED")}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                className={classes.bannerContainer}
                style={{ background: "white", color: "black" }}
              >
                <img
                  src={instantWin.image_url}
                  alt="instant-win-img"
                  className={classes.bannerImg}
                />
                {showCountdown && (!isJoinStart || !isJoinEnd) && (
                  <Box className={classes.bannerCountdown}>
                    <Typography variant="caption">
                      {isJoinStart
                        ? getTranslation("label.ENDS_IN")
                        : getTranslation("label.STARTS_IN")}{" "}
                      <span>
                        <b>
                          {countdown.day}:{formatTwoDigits(countdown.hour)}:
                          {formatTwoDigits(countdown.min)}:
                          {formatTwoDigits(countdown.sec)}
                        </b>
                      </span>
                    </Typography>
                  </Box>
                )}
                <Box mt={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={0.5}
                    flexWrap="wrap"
                  >
                    <Box mr={1} mt={0.5}>
                      <Typography variant="body1">
                        <b>{instantWin.title}</b>
                      </Typography>
                    </Box>
                    <Box className={classes.label} mt={0.5}>
                      <Typography variant="body2">
                        <b>{getTranslation("label.INSTANT_WIN")}</b>
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body1">
                    {instantWin.description}
                  </Typography>
                </Box>
                <Box mt={2} mb={1}>
                  <button
                    className={classes.button}
                    style={{
                      border: "1px solid black",
                    }}
                  >
                    <Typography
                      variant="body1"
                      style={{ textTransform: "capitalize", zIndex: 1 }}
                    >
                      <b>
                        {getTranslation(
                          isJoinStart ? "label.PLAY_NOW" : "label.EXPLORE_NOW"
                        )}
                      </b>
                    </Typography>
                  </button>
                </Box>
                {isEnded && (
                  <Box className={classes.overlay}>
                    <Typography variant="body1">
                      {getTranslation("label.ENDED")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}
